import { format } from 'svelte-i18n';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { devices_settings } from 'src/stores/device-runtime/status';
import { real_id } from 'src/helpers/interface_helpers';
import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
import type { CameraCapabilities, CameraSettings } from '../types/camera-settings';
import type { CameraSoundName } from '../types/camera-sounds';
import { getCameraComponentKey } from './camera-helpers';

export const getCameraConfig = (device: Device): CameraSettings | undefined => {
	const id = real_id(device.id);
	return readDeviceSettings(id)?.[getCameraComponentKey(device)];
};

export const getCameraCapabilities = async (device: Device): Promise<CameraCapabilities | null> => {
	const channel = device?.channel || 0;
	const res: SettingRequest = await gen2Request(device, 'Camera.GetCapabilities', { id: channel });
	if (!res?.isok) return null;
	return (res.data ?? res.result) as CameraCapabilities;
};

/**
 * Play one of the built-in sounds on the camera speaker.
 *
 * @returns Whether the camera accepted the command.
 */
export const playCameraSound = async (device: Device, sound: CameraSoundName): Promise<boolean> => {
	const channel = device?.channel || 0;
	const res: SettingRequest = await gen2Request(device, 'Camera.PlaySound', {
		id: channel,
		sound,
	});
	return !!res?.isok;
};

export const saveCameraConfig = async (
	device: Device,
	config: Partial<CameraSettings>,
	successMessage: string,
): Promise<boolean> => {
	const _format = get(format);
	const channel = device?.channel || 0;
	const cameraKey = getCameraComponentKey(device);

	const res: SettingRequest = await gen2Request(device, 'Camera.SetConfig', {
		id: channel,
		config,
	});
	onSettingsResponse(res, device, {
		success: successMessage,
		danger: _format('camera_settings_save_error', {
			default: 'Could not apply camera settings. Please retry.',
		}),
	});

	if (!res?.isok) return false;

	const id = real_id(device.id);
	const settings = devices_settings.getByRealId(device.id);
	devices_settings.update_devices_settings(id, {
		...settings,
		[cameraKey]: { ...settings[cameraKey], ...config },
	});
	return true;
};
